'use client'

import { useCallback, useEffect, useRef, useState } from 'react'
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material'
import { Icon } from '@media-components'
import { MenuHOC } from '@components/common'
import { Show } from '@utils/hoc'
import { CustomStyles, getStyles } from '@utils/styles'
import { Noop } from '@utils/typescript'
import { initializeAudioPlayer } from '../model/audio-player.utils'
import defaultStyles, { StylesClassNames, Wrapper } from '../styles'
import { AudioElementEventProps, AudioElementNativeProps } from '../types'
import AudioControls from './AudioControls'
import { AudioVisualizer, AudioVisualizerDataProps } from './AudioVisualizer'
import { MinifiedTrack } from './MinifiedTrack'
import { Timer } from './Timer'

export type AudioPlayerProps = {
  /**
   * Displays a minified version of the audio player, with only the play/pause button, track bar and timestamp. Defaults to `false`
   */
  minified?: boolean
  /**
   * The number of seconds to skip forward/backward. Defaults to `5`
   */
  skipDuration?: number
  /**
   * To show close icon in audio player
   */
  onClose?: Noop
  /**
   * To show audio player in loading state
   */
  loading?: boolean
  /**
   * To show audio player in buffering state
   */
  buffering?: boolean
  /**
   * Function that will be called on click of open in minified state
   */
  onMaximize?: Noop
  /**
   * Custom styles to override the default styles
   */
  csx?: CustomStyles<StylesClassNames>
  /**
   * event fn props
   */
  audioEventProps?: AudioElementEventProps
} & Partial<AudioVisualizerDataProps> &
  AudioElementNativeProps

function AudioPlayer({
  // Audio player props
  onClose,
  onMaximize,
  minified = false,
  skipDuration = 5,
  loading = false,
  buffering = false,
  csx,

  // Audio Visualizer props
  barWidth = 4,
  maxTrackWidth = 1000,
  height = 24,

  // audio element events
  audioEventProps,

  // native props
  src,
  autoplay = false,
  ...restNativeAudioProps
}: AudioPlayerProps) {
  const styles = getStyles(defaultStyles, csx)

  const audioRef = useRef<HTMLAudioElement | undefined>(undefined)

  const [currentTime, setCurrentTime] = useState<number>(0)
  const [duration, setDuration] = useState<number>(0)
  const [isPlaying, setIsPlaying] = useState<boolean>(false)

  const changePlaybackRate = (speed = 1): void => {
    if (audioRef.current) {
      audioRef.current.playbackRate = speed
    }
  }

  const setAudioTime = (time: number): void => {
    const audio = audioRef.current
    if (audio) {
      audio.currentTime = time
    }
    setCurrentTime(time)
  }

  const onAudioEnded = (): void => {
    setIsPlaying(false)
    setAudioTime(0)
  }

  const pauseAudio = (): void => {
    audioRef.current?.pause()
    setIsPlaying(false)
  }

  const skipForwards = (): void => {
    setAudioTime(Math.min(duration, currentTime + skipDuration))
  }
  const skipBackwards = (): void => {
    setAudioTime(Math.max(0, currentTime - skipDuration))
  }

  const seekTo = (time: number): void => {
    setAudioTime(time)
  }

  const setAudioDuration = () => {
    const audio = audioRef.current

    if (!audio || !audio.src) return

    if (audio.duration && audio.duration !== Infinity) {
      setDuration(audio.duration)
    }
  }

  const handleAnimation = useCallback(() => {
    const audio = audioRef.current
    if (duration && audio) {
      const handleTimeUpdates = (): void => {
        // Handling pause once the audio play is completed
        if (audio.currentTime >= duration) {
          pauseAudio()
          setAudioTime(0)
          return
        }
        setCurrentTime(audio.currentTime)

        requestAnimationFrame(handleTimeUpdates)
      }
      requestAnimationFrame(handleTimeUpdates)
    }
  }, [duration])

  const playAudio = (): void => {
    const audio = audioRef.current
    if (!audio || !audio.src) return

    void audio.play()

    setIsPlaying(true)

    if (duration) {
      handleAnimation()
    }
  }

  /** Setting current time when duration is available */
  useEffect(() => {
    handleAnimation()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [duration, autoplay])

  useEffect(() => {
    const { ...restAudioEventProps } = audioEventProps || {}

    const audio = initializeAudioPlayer(
      { src, autoplay, ...restNativeAudioProps },
      {
        ...restAudioEventProps,
        onplay: () => {
          if (autoplay) setIsPlaying(true)
          setAudioDuration()
        },
        // ondurationchange: ()q => setAudioDuration(),
      },
      audioRef.current
    )

    if (audio) {
      audioRef.current = audio

      audioRef.current.addEventListener('ended', onAudioEnded)
    }

    return () => {
      if (audioRef.current) {
        audioRef.current.pause()
        audioRef.current.removeEventListener('ended', onAudioEnded)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [src])

  /** Setting duration at the end of buffering */
  useEffect(() => {
    if (!buffering && !loading) {
      setAudioDuration()
    }
  }, [buffering, loading])

  return (
    <Wrapper minified={minified} {...styles('wrapper')}>
      <AudioControls
        isPlaying={isPlaying}
        onPauseClick={pauseAudio}
        onPlayClick={playAudio}
        onSkipBackwardClick={skipBackwards}
        onSkipForwardClick={skipForwards}
        disabled={loading}
        buffering={buffering}
        // allowSkip={!minified}
        skipDuration={skipDuration}
      />

      <Show>
        <Show.When isTrue={loading || buffering}>
          <CircularProgress size={20} sx={{ maxHeight: 'fit-content' }} />
        </Show.When>

        <Show.When isTrue={minified}>
          <Tooltip title="Open current Play">
            <Button
              endIcon={
                <Icon
                  iconName="ArrowLink"
                  fontSize="small"
                  colorValues="text.primary"
                />
              }
              variant="text"
              onClick={onMaximize}
            >
              Open
            </Button>
          </Tooltip>
        </Show.When>

        <Show.Else>
          <Timer seconds={currentTime} {...styles('timer')} />
        </Show.Else>
      </Show>

      <Box
        {...styles('trackWrapper', {
          width: '100%',
        })}
      >
        {!loading && !buffering && (
          <AudioVisualizer
            hidden={minified}
            duration={duration}
            currentTime={currentTime}
            maxTrackWidth={maxTrackWidth}
            barWidth={barWidth}
            height={height}
            csx={csx}
          />
        )}

        <MinifiedTrack
          duration={duration}
          currentTime={currentTime}
          onSeek={seekTo}
          height={height}
          isPlaying={isPlaying}
          loading={loading || buffering}
          minified={minified}
          csx={csx}
        />
      </Box>

      <MenuHOC
        initialSelected={1}
        renderAnchor={({ openMenu, selectedValue }) => (
          <Button
            sx={{ height: 30, mx: { xs: -1, md: -2 } }}
            onClick={openMenu}
            variant="text"
          >
            {selectedValue}x
            <Icon iconName="ArrowDown" fontSize="extraSmall" />
          </Button>
        )}
        onMenuSelect={changePlaybackRate}
        menuItemProps={{ sx: { justifyContent: 'center' } }}
        menuItems={[
          { label: '0.5x', value: 0.5 },
          { label: '1x', value: 1 },
          { label: '1.5x', value: 1.5 },
          { label: '1.75x', value: 1.7 },
          { label: '2x', value: 2 },
        ]}
      />

      {onClose && (
        <IconButton onClick={onClose} mode="light">
          <Icon iconName="Cross" />
        </IconButton>
      )}
    </Wrapper>
  )
}

export { AudioPlayer }
